/**
 * Tooltip — the one hint surface (DESIGN.md, Tooltips).
 *
 * The component owns its trigger: it renders the described text with the house
 * treatment (a dotted underline), visible AT REST rather than on
 * hover, so nothing carries a tooltip a reader cannot see. Callers pass the
 * text, not a wrapper element — an unmarked span, header, or icon is exactly
 * the undiscoverable trigger this replaces.
 *
 * POSITIONING IS OURS, NOT ASTRYX'S (#346). Astryx's Tooltip places its bubble
 * with `position-anchor`/`position-area` and nothing else — no top/left, no
 * measurement. iOS Safari does not support CSS anchor positioning, so all three
 * declarations are dropped at parse time and what remains is the UA popover box
 * (`position: fixed; inset: 0`) with Astryx's own margins zeroed: the bubble
 * lands flush in the viewport's TOP-LEFT CORNER, over the nav bar, nowhere near
 * its trigger. `position-try-fallbacks` cannot rescue it — same unsupported
 * spec. This is the failure DESIGN.md already records for Astryx's
 * `DropdownMenu`, and it is why every popup in admin positions itself.
 *
 * The bubble therefore portals to the theme wrapper (`[data-astryx-theme]`,
 * so it keeps the theme's face) and is placed from the
 * trigger's `getBoundingClientRect()`: preferred above, flipped below when it
 * would clear the viewport top, and clamped on both axes. A portal (rather than
 * the house `relative` + `absolute` pattern) because a tooltip must escape the
 * `overflow` ancestors it lives inside — the gear editor and any scrolling
 * table would otherwise clip it. That is the one job `position-area` was doing,
 * done in JS.
 *
 * Reachability without a pointer: the trigger is focusable, keyboard focus
 * opens the bubble, and a touch tap force-opens it — hover is meaningless under
 * `(hover: none)` and a tap sets no `:focus-visible`, so touch would otherwise
 * never reach a tooltip at all. The tap-opened bubble closes on the next
 * pointer down anywhere; Escape closes either. A pointer may travel from the
 * trigger onto the bubble without it vanishing (WCAG 1.4.13, hoverable).
 *
 * The bubble's colour and type stay the theme's (`shared/theme/hireos.ts`,
 * `components.tooltip`), reached through the same stable `astryx-tooltip`
 * class Astryx would have emitted — DESIGN.md bans per-app overrides, and
 * dropping Astryx's positioning is not a licence to fork its palette. Only
 * geometry lives here: the bubble sizes to its text (`w-max` under a ~34ch cap,
 * never a fixed width, so short content does not wrap) and `break-words` is the
 * emergency break for a single overlong token only — never `break-all`.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { stableClassName } from "@astryxdesign/core/naming";
import { useDismissable } from "../../lib/use-dismissable";

import styles from "./Tooltip.module.css";

/** Gap between the trigger and the bubble. */
const GAP = 6;
/** Minimum clearance from every viewport edge. */
const MARGIN = 8;
/**
 * Grace period before hiding on pointer-leave, so the pointer can cross the
 * gap from the trigger onto the bubble without it disappearing (WCAG 1.4.13).
 */
const HOVER_BRIDGE_MS = 100;

interface Position {
  top: number;
  left: number;
}

interface TooltipProps {
  /** One sentence explaining what the thing means or what its value affects. */
  content: string;
  /** The described text — the Tooltip renders it as the trigger. */
  children: ReactNode;
  className?: string;
  /**
   * `text` (the default) is the described text with its dotted underline.
   * `control` wraps a disabled control instead: the one case a control
   * carries a tooltip (DESIGN.md, Tooltips) — a switch greyed out because
   * nothing is connected says on hover what would make it usable. No
   * underline, because there is no text to underline.
   *
   * `button` wraps a working icon button (+, ×) that has no words of its
   * own: the button keeps its click and its one tab stop, and its focus
   * reaches this wrapper by bubbling.
   */
  trigger?: "text" | "control" | "button";
}

export function Tooltip({
  content,
  children,
  className = "",
  trigger = "text",
}: TooltipProps) {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = useState(false);
  /** True while the bubble was opened by a tap — the only mode a pointer down
   *  anywhere dismisses. */
  const [tapped, setTapped] = useState(false);
  /** Null until measured: the bubble renders hidden for one frame so it can be
   *  measured, rather than flashing at the corner the bug put it in. */
  const [pos, setPos] = useState<Position | null>(null);

  const cancelHide = useCallback(() => {
    if (hideTimer.current === null) return;
    clearTimeout(hideTimer.current);
    hideTimer.current = null;
  }, []);

  const close = useCallback(() => {
    cancelHide();
    setOpen(false);
    setTapped(false);
    setPos(null);
  }, [cancelHide]);

  const show = useCallback(() => {
    cancelHide();
    setOpen(true);
  }, [cancelHide]);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimer.current = setTimeout(close, HOVER_BRIDGE_MS);
  }, [cancelHide, close]);

  useEffect(() => cancelHide, [cancelHide]);

  // Escape closes either mode; a pointer down anywhere closes a tapped-open
  // bubble only (a mouse user clicking elsewhere is already covered by leave).
  useDismissable({
    open,
    onDismiss: close,
    pointer: tapped ? "any" : "none",
  });

  /**
   * Place the bubble from the trigger's rect: above by preference, below when
   * above would clear the viewport top, then clamped on both axes so a trigger
   * at an edge still yields a fully visible bubble.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;

    const t = trigger.getBoundingClientRect();
    const { width, height } = bubble.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = t.top - height - GAP;
    if (top < MARGIN) top = t.bottom + GAP;
    top = Math.max(MARGIN, Math.min(top, vh - height - MARGIN));

    let left = t.left + t.width / 2 - width / 2;
    left = Math.max(MARGIN, Math.min(left, vw - width - MARGIN));

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, content, place]);

  // The trigger moves under a scrolling page or a resized window; a bubble
  // left behind is worse than no bubble. Capture, so inner scrollers count.
  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  const bubble = open
    ? createPortal(
        <div
          ref={bubbleRef}
          id={id}
          role="tooltip"
          className={`${stableClassName("tooltip")} ${styles.bubble}`}
          style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            visibility: pos ? "visible" : "hidden",
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        >
          {content}
        </div>,
        // Into the theme wrapper, not <body>: the face is set on the
        // wrapper (styles.css, "The document"), and a bubble outside it
        // drew in the browser's default serif. Fixed positioning still
        // escapes every overflow ancestor from here.
        triggerRef.current?.closest<HTMLElement>("[data-astryx-theme]") ??
          document.body,
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={trigger === "button" ? undefined : 0}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => {
          // Hover is simulated on touch and would eat the tap.
          if (window.matchMedia?.("(hover: none)").matches) return;
          show();
        }}
        onMouseLeave={scheduleHide}
        onFocus={(e) => {
          // Keyboard focus only — a tap and a programmatic focus both focus
          // without ever matching :focus-visible.
          if (!matchesFocusVisible(e.target)) return;
          show();
        }}
        onBlur={scheduleHide}
        onPointerUp={(e) => {
          if (e.pointerType === "mouse") return;
          setTapped(true);
          show();
        }}
        className={`${
          trigger === "control"
            ? styles.controlTrigger
            : trigger === "button"
              ? styles.buttonTrigger
              : styles.trigger
        } ${className}`.trim()}
      >
        {children}
      </span>
      {bubble}
    </>
  );
}

/** `:focus-visible` is not a selector every engine (or jsdom) can match. */
function matchesFocusVisible(el: Element): boolean {
  try {
    return el.matches(":focus-visible");
  } catch {
    return false;
  }
}
